"use client";

import {
	getGroundingPlatformDisplayName,
	getGroundingPlatformLogo,
} from "@repo/core/client";
import { cn } from "@ui/lib";
import { ChevronDown, ChevronUp, Users, Zap } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useState } from "react";
import { RankBadge } from "./RankBadge";
import type { GroundingSearchItem, GroundingSearchTableProps } from "./types";
import {
	getActiveEngine,
	getBestCompetitorRank,
	getBrandRankForItem,
	getBrandUrlForItem,
	getCompetitorRankForItem,
	getCompetitorUrlForItem,
	isGapForItem,
} from "./utils";

function PlatformIcons({ platforms }: { platforms?: string[] }) {
	if (!platforms?.length) return null;

	const unique = Array.from(new Set(platforms));
	return (
		<span
			className="inline-flex items-center gap-0.5"
			title={unique.map(getGroundingPlatformDisplayName).join(", ")}
		>
			{unique.slice(0, 3).map((p) => {
				const src = getGroundingPlatformLogo(p);
				if (!src) return null;
				return (
					<Image
						key={p}
						src={src}
						alt={getGroundingPlatformDisplayName(p)}
						width={16}
						height={16}
						className="rounded-sm flex-shrink-0"
					/>
				);
			})}
		</span>
	);
}

/**
 * `site:domain.com` marker for a scoped search.
 *
 * Colour follows WHOSE site the model chose, matching this folder's inline-pill
 * idiom (`px-2 py-1 rounded text-xs font-medium bg-<hue>-100 text-<hue>-800`):
 * blue reads as ours, red as a competitor's, gray as everyone else's. The title
 * carries the "why you can't act on this" explanation, because the row's rank
 * cells will all read N/A and that otherwise looks like missing data.
 */
function SiteScopePill({ item }: { item: GroundingSearchItem }) {
	if (!item.siteScopeDomain) return null;

	const tone =
		item.siteScopeClass === "own"
			? "bg-blue-100 text-blue-800"
			: item.siteScopeClass === "competitor"
				? "bg-red-100 text-red-800"
				: "bg-gray-100 text-gray-700";

	const whose =
		item.siteScopeClass === "own"
			? "your site"
			: item.siteScopeClass === "competitor"
				? `${item.siteScopeCompetitorName ?? "a competitor"}'s site`
				: "a third-party site";

	return (
		<span
			className={cn(
				"inline-flex items-center px-2 py-1 rounded text-xs font-medium",
				tone,
			)}
			title={`AI searched ${whose} directly instead of the open web. There's no ranking to win here — this is evidence about who it trusts, not an opportunity.`}
		>
			site:{item.siteScopeDomain}
		</span>
	);
}

export function GroundingSearchTable({
	items,
	viewMode,
	selectedItem,
	onItemClick,
	organizationSlug,
	propertyId,
	maxItems,
	showExpandButton = true,
}: GroundingSearchTableProps) {
	const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
	const [showAll, setShowAll] = useState(false);

	const toggleExpanded = (id: string, e: React.MouseEvent) => {
		e.stopPropagation();
		const newExpanded = new Set(expandedItems);
		if (newExpanded.has(id)) {
			newExpanded.delete(id);
		} else {
			newExpanded.add(id);
		}
		setExpandedItems(newExpanded);
	};

	const displayItems =
		maxItems && !showAll ? items.slice(0, maxItems) : items;
	const hasMore = maxItems && items.length > maxItems;

	if (items.length === 0) {
		return (
			<div className="text-center py-8 text-gray-500">
				No grounding searches match your filters
			</div>
		);
	}

	if (viewMode === "compact") {
		return (
			<CompactView
				items={displayItems}
				onItemClick={onItemClick}
				organizationSlug={organizationSlug}
				propertyId={propertyId}
				showAll={showAll}
				hasMore={!!hasMore}
				totalItems={items.length}
				onShowAllToggle={() => setShowAll(!showAll)}
				showExpandButton={showExpandButton}
			/>
		);
	}

	return (
		<div className="space-y-3">
			{displayItems.map((item, index) => (
				<GroundingSearchRow
					key={item.id}
					item={item}
					index={index}
					isExpanded={expandedItems.has(item.id)}
					isSelected={selectedItem?.id === item.id}
					onToggleExpand={(e) => toggleExpanded(item.id, e)}
					onItemClick={onItemClick}
					organizationSlug={organizationSlug}
					propertyId={propertyId}
				/>
			))}

			{hasMore && showExpandButton && (
				<button
					type="button"
					onClick={() => setShowAll(!showAll)}
					className="w-full py-3 text-sm font-medium text-amber-700 hover:text-amber-800 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors"
				>
					{showAll
						? "Show Less"
						: `Show All ${items.length} Searches`}
				</button>
			)}
		</div>
	);
}

interface GroundingSearchRowProps {
	item: GroundingSearchItem;
	index: number;
	isExpanded: boolean;
	isSelected: boolean;
	onToggleExpand: (e: React.MouseEvent) => void;
	onItemClick?: (item: GroundingSearchItem) => void;
	organizationSlug?: string;
	propertyId?: string;
}

function GroundingSearchRow({
	item,
	index,
	isExpanded,
	isSelected,
	onToggleExpand,
	onItemClick,
	organizationSlug,
	propertyId,
}: GroundingSearchRowProps) {
	const isGap = isGapForItem(item);
	const engine = getActiveEngine(item);
	const bestCompetitor = getBestCompetitorRank(
		item.competitorRankings,
		engine,
	);

	const handleClick = () => {
		if (onItemClick) {
			onItemClick(item);
		}
	};

	return (
		<div
			className={cn(
				"border rounded-lg overflow-hidden transition-all",
				isGap
					? "border-orange-200 bg-orange-50/30"
					: "border-gray-200 bg-white",
				isSelected && "ring-2 ring-blue-400",
				onItemClick && "cursor-pointer hover:shadow-md",
			)}
			onClick={handleClick}
			// Keyboard parity with the mouse handler above. Only a row that
			// actually does something on click becomes focusable, so a
			// read-only list doesn't grow phantom tab stops.
			onKeyDown={(e) => {
				if (!onItemClick) return;
				if (e.key === "Enter" || e.key === " ") {
					e.preventDefault();
					handleClick();
				}
			}}
			role={onItemClick ? "button" : undefined}
			tabIndex={onItemClick ? 0 : undefined}
		>
			{/* Header Row */}
			<div className="p-4">
				<div className="flex items-start justify-between gap-4">
					<div className="flex-1 min-w-0">
						{/* Query with rank indicator and platform icon(s) */}
						<div className="flex items-center gap-3 mb-2">
							<span
								className={cn(
									"inline-flex items-center justify-center w-7 h-7 rounded-full text-sm font-bold flex-shrink-0",
									isGap
										? "bg-orange-200 text-orange-800"
										: "bg-green-200 text-green-800",
								)}
							>
								{index + 1}
							</span>
							<h4 className="font-medium text-gray-900 truncate">
								"{item.searchQuery}"
							</h4>
							<PlatformIcons platforms={item.platforms} />
							<SiteScopePill item={item} />
						</div>

						{/* Meta info row */}
						<div className="flex flex-wrap items-center gap-2 ml-10">
							<span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
								{item.usageCount}{" "}
								{item.usageCount === 1 ? "query" : "queries"}
							</span>

							{item.competitorRankings.length > 0 && (
								<span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-700">
									<Users className="h-3 w-3" />
									{item.competitorRankings.length}
								</span>
							)}

							{isGap && (
								<span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-orange-100 text-orange-800">
									⚠️ Gap
								</span>
							)}

							{/* Optimize for AI citation. Same gate as the detail drawer: the Citation
                  Optimizer is ChatGPT-only in v1, so only offer it for fan-outs that
                  ChatGPT (openai) generated, and only where we have the org/property
                  context to build the link. stopPropagation so it doesn't also fire the
                  row's select handler. */}
							{organizationSlug &&
								propertyId &&
								(item.platforms ?? []).includes("openai") && (
									<Link
										href={`/app/${organizationSlug}/properties/${propertyId}/citation-optimizer?fanOut=${item.id}`}
										onClick={(e) => e.stopPropagation()}
										className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-fuchsia-100 text-fuchsia-800 transition-colors hover:bg-fuchsia-200"
									>
										<Zap className="h-3 w-3" />
										Optimize
									</Link>
								)}
						</div>
					</div>

					{/* Right side: Rankings & Impact */}
					<div className="flex items-center gap-4">
						{/* Brand Ranking */}
						<div className="hidden md:block">
							<RankBadge
								rank={getBrandRankForItem(item)}
								engine={engine}
								url={getBrandUrlForItem(item)}
							/>
						</div>

						{/* Impact Score */}
						<div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-lg px-3 py-1 border border-orange-200 text-center min-w-[70px]">
							<div className="text-xs text-orange-700">
								Impact
							</div>
							<div className="text-lg font-bold text-orange-900">
								{Math.round(item.impactScore)}
							</div>
						</div>

						{/* Expand button */}
						<button
							type="button"
							onClick={onToggleExpand}
							className="p-1 hover:bg-gray-100 rounded transition-colors"
						>
							{isExpanded ? (
								<ChevronUp className="h-5 w-5 text-gray-400" />
							) : (
								<ChevronDown className="h-5 w-5 text-gray-400" />
							)}
						</button>
					</div>
				</div>

				{/* Mobile: Show rankings below */}
				<div className="md:hidden mt-3 ml-10">
					<RankBadge
						rank={getBrandRankForItem(item)}
						engine={engine}
						url={getBrandUrlForItem(item)}
					/>
				</div>
			</div>

			{/* Expanded Details */}
			{isExpanded && (
				// Not a control: both handlers exist only to keep interaction
				// inside the expanded panel from re-triggering the row that
				// owns it (the row now responds to Enter/Space as well as
				// click, so stopping the key event matters too).
				<div
					className="p-4 pt-0 border-t border-gray-200 bg-white space-y-4"
					onClick={(e) => e.stopPropagation()}
					onKeyDown={(e) => e.stopPropagation()}
				>
					{/* Discovery Queries */}
					{item.discoveryQueries.length > 0 && (
						<div>
							<h5 className="text-sm font-medium text-gray-700 mb-2">
								Used to answer these queries:
							</h5>
							<div className="space-y-1">
								{item.discoveryQueries.map((dq) => (
									<div
										key={dq.id}
										className="flex items-center gap-2"
									>
										{organizationSlug && propertyId ? (
											<Link
												href={`/app/${organizationSlug}/properties/${propertyId}/discovery-queries/${dq.id}`}
												className="text-sm text-blue-600 hover:text-blue-800 hover:underline bg-blue-50 rounded px-3 py-2 flex-1"
												onClick={(e) =>
													e.stopPropagation()
												}
											>
												"{dq.query}"
											</Link>
										) : (
											<span className="text-sm text-gray-700 bg-blue-50 rounded px-3 py-2 flex-1">
												"{dq.query}"
											</span>
										)}
									</div>
								))}
							</div>
						</div>
					)}

					{/* Competitor Rankings */}
					{item.competitorRankings.length > 0 && (
						<div>
							<h5 className="text-sm font-medium text-gray-700 mb-2">
								Competitor Rankings:
							</h5>
							<div className="grid grid-cols-1 md:grid-cols-2 gap-2">
								{item.competitorRankings
									.sort(
										(a, b) =>
											(getCompetitorRankForItem(
												item,
												a,
											) ?? Number.POSITIVE_INFINITY) -
											(getCompetitorRankForItem(
												item,
												b,
											) ?? Number.POSITIVE_INFINITY),
									)
									.slice(0, 6)
									.map((comp) => (
										<div
											key={comp.competitorId}
											className="bg-gray-50 rounded px-3 py-2 border border-gray-200"
										>
											<div className="font-medium text-gray-900 text-sm mb-1">
												{comp.competitorName}
												<span className="text-gray-400 font-normal ml-1">
													({comp.competitorDomain})
												</span>
											</div>
											<RankBadge
												rank={getCompetitorRankForItem(
													item,
													comp,
												)}
												engine={engine}
												url={getCompetitorUrlForItem(
													item,
													comp,
												)}
												size="sm"
											/>
										</div>
									))}
							</div>
							{item.competitorRankings.length > 6 && (
								<p className="text-xs text-gray-500 mt-2">
									+{item.competitorRankings.length - 6} more
									competitors
								</p>
							)}
						</div>
					)}
				</div>
			)}
		</div>
	);
}

interface CompactViewProps {
	items: GroundingSearchItem[];
	onItemClick?: (item: GroundingSearchItem) => void;
	organizationSlug?: string;
	propertyId?: string;
	showAll: boolean;
	hasMore: boolean;
	totalItems: number;
	onShowAllToggle: () => void;
	showExpandButton: boolean;
}

function CompactView({
	items,
	onItemClick,
	showAll,
	hasMore,
	totalItems,
	onShowAllToggle,
	showExpandButton,
}: CompactViewProps) {
	return (
		<div className="space-y-2">
			{items.map((item) => {
				const isGap = isGapForItem(item);
				const engine = getActiveEngine(item);
				return (
					<div
						key={item.id}
						className={cn(
							"flex items-center justify-between p-3 rounded-lg border transition-colors",
							isGap
								? "bg-orange-50/50 border-orange-200 hover:bg-orange-100/50"
								: "bg-green-50/50 border-green-200 hover:bg-green-100/50",
							onItemClick && "cursor-pointer",
						)}
						onClick={() => onItemClick?.(item)}
						onKeyDown={(e) => {
							if (!onItemClick) return;
							if (e.key === "Enter" || e.key === " ") {
								e.preventDefault();
								onItemClick(item);
							}
						}}
						role={onItemClick ? "button" : undefined}
						tabIndex={onItemClick ? 0 : undefined}
					>
						<div className="flex-1 min-w-0 flex items-center gap-2">
							<span className="text-sm text-gray-900 truncate">
								"{item.searchQuery}"
							</span>
							<PlatformIcons platforms={item.platforms} />
							<SiteScopePill item={item} />
						</div>
						<div className="flex items-center gap-2 ml-3">
							<RankBadge
								rank={getBrandRankForItem(item)}
								engine={engine}
								url={getBrandUrlForItem(item)}
								size="sm"
							/>
						</div>
					</div>
				);
			})}

			{hasMore && showExpandButton && (
				<button
					type="button"
					onClick={onShowAllToggle}
					className="w-full py-2 text-sm text-gray-600 hover:text-gray-800"
				>
					{showAll
						? "Show Less"
						: `+${totalItems - items.length} more`}
				</button>
			)}
		</div>
	);
}
